import React, { useState, useEffect } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { API_BASE_URL } from './services/api/config'; 

const KeyGuardModal = ({ isOpen, onClose, onSuccess, mode: initialMode = 'verify' }) => {
  const [status, setStatus] = useState('idle'); // idle, loading, error, success
  const [errorMessage, setErrorMessage] = useState('');
  
  const [currentMode, setCurrentMode] = useState(initialMode);

  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setErrorMessage('');
      setCurrentMode(initialMode);
    }
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  // --- CENTRALIZED LOGOUT FUNCTION ---
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user'); 
    window.location.href = '/login';
  };

  // Helper to handle fetch errors safely
  const handleFetchErrors = async (response) => {
    if (!response.ok) {
      // Auto-logout on 401/403
      if (response.status === 401 || response.status === 403) {
        console.warn("Session invalid, forcing logout.");
        handleLogout();
        throw new Error('Session expired. Logging out...');
      }

      const data = await response.json();
      const error = new Error(data.message || data.error || 'Server error');
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return response.json();
  };

  // --- 1. REGISTER ---
  const handleRegister = async () => {
    setStatus('loading');
    setErrorMessage('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
          handleLogout(); 
          return;
      }

      const startResp = await fetch(`${API_BASE_URL}/auth/register-start`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const options = await handleFetchErrors(startResp);

      // --- FIX: WRAP OPTIONS IN { optionsJSON: ... } ---
      const attResp = await startRegistration({ optionsJSON: options });
      // ------------------------------------------------

      const finishResp = await fetch(`${API_BASE_URL}/auth/register-finish`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(attResp),
      });

      const verificationJSON = await handleFetchErrors(finishResp);
      
      if (verificationJSON.verified) {
        // Check for new token
        if (verificationJSON.token) {
            localStorage.setItem('token', verificationJSON.token);
        }

        setStatus('success');
        setTimeout(() => {
            onSuccess(); 
            onClose();
        }, 1500);
      } else {
        throw new Error('Registration failed.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || 'Registration failed');
      setStatus('error');
    }
  };

  // --- 2. VERIFY ---
  const handleVerify = async () => {
    setStatus('loading');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
          handleLogout();
          return;
      }

      const startResp = await fetch(`${API_BASE_URL}/auth/reverify-start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (startResp.status === 400) {
        const errorData = await startResp.json();
        if (errorData.error === 'No security key registered' || errorData.code === 'NO_AUTHENTICATOR_FOUND') {
          console.log("User has no keys. Switching to registration mode.");
          setStatus('idle');
          setCurrentMode('register'); 
          return; 
        }
      }

      const options = await handleFetchErrors(startResp);

      if (!options || !options.challenge) {
        throw new Error('Invalid server response.');
      }

      // --- FIX: WRAP OPTIONS IN { optionsJSON: ... } ---
      const asseResp = await startAuthentication({ optionsJSON: options });
      // ------------------------------------------------

      const finishResp = await fetch(`${API_BASE_URL}/auth/reverify-finish`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ data: asseResp }),
      });

      const verificationJSON = await handleFetchErrors(finishResp);

      if (verificationJSON.verified) {
        // Check for new token
        if (verificationJSON.token) {
            localStorage.setItem('token', verificationJSON.token);
        }

        setStatus('success');
        setTimeout(() => {
            onSuccess(); 
            onClose();
        }, 1000);
      } else {
        throw new Error('Verification failed.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || 'Verification failed');
      setStatus('error');
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={{ fontSize: '40px', marginBottom: '10px' }}>
          {status === 'success' ? '✅' : '🔒'}
        </div>

        <h2 style={{marginBottom: '10px', color: '#1f2937'}}>
          {currentMode === 'register' ? 'Setup Security Key' : 'Session Paused'}
        </h2>
        
        <p style={{marginBottom: '20px', color: '#4b5563', lineHeight: '1.5'}}>
          {currentMode === 'register' 
            ? 'You have been inactive. To continue, please register your fingerprint or FaceID now.' 
            : 'For your security, your session has been paused due to inactivity. Verify your identity to continue.'}
        </p>

        {status === 'error' && (
          <div style={styles.errorBox}>
            {errorMessage}
          </div>
        )}
        
        {status === 'success' && (
          <div style={{color: '#059669', fontWeight: 'bold', marginBottom: '15px'}}>
            Identity Verified. Resuming session...
          </div>
        )}

        <div style={styles.buttons}>
          {currentMode === 'register' ? (
             <button 
               onClick={handleRegister} 
               disabled={status === 'loading' || status === 'success'}
               style={styles.primaryBtn}
             >
               {status === 'loading' ? 'Scanning...' : 'Register & Continue'}
             </button>
          ) : (
             <button 
               onClick={handleVerify} 
               disabled={status === 'loading' || status === 'success'}
               style={styles.primaryBtn}
             >
               {status === 'loading' ? 'Verifying...' : 'Verify to Resume'}
             </button>
          )}
          
          <button 
            onClick={handleLogout} 
            style={styles.cancelBtn} 
            disabled={status === 'loading' || status === 'success'}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)', 
    backdropFilter: 'blur(4px)', 
    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
  },
  modal: {
    backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '400px', textAlign: 'center',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  buttons: { marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  errorBox: {
    backgroundColor: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '6px', marginBottom: '15px', fontSize: '14px'
  },
  primaryBtn: {
    backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '6px', 
    cursor: 'pointer', fontSize: '16px', fontWeight: '600', transition: 'background 0.2s'
  },
  cancelBtn: {
    backgroundColor: 'transparent', color: '#6b7280', border: 'none', padding: '8px 16px', borderRadius: '6px', 
    cursor: 'pointer', fontSize: '14px', textDecoration: 'underline'
  }
};

export default KeyGuardModal;
